import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core'
import { Post } from 'types'
import { PostActions } from 'actions/post-actions'
import { CustomerWriterAccessActions } from 'actions/customer-writer-access-actions'
import { SubmissionMutations } from 'src/modules/submissions/submission.mutations'

@Component({
  selector: `rate-post-modal`,
  template: `
    <modal (outsideClick)="onDone()">
      <div class="text-center" *ngIf="step === 'rate'">
        <i class="fas fa-star giant-text text-primary"></i>
        <h4 class="mt-3">Rate {{ writerFirstName() }}'s submission</h4>
        <hr class="brand-hr">
        <p *ngIf="action === 'purchase'">
          You've accepted this submission. Rating it will help our writers learn how to deliver better content to you in the future.
        </p>
        <p *ngIf="action === 'decline'">
          You've declined this submission. Rating it will help our writers learn how to deliver better content to you in the future.
        </p>
        <i class="bigger-text text-primary cursor-pointer fa-star" *ngFor="let i of stars"
           [class.fas]="i <= highlightedStar"
           [class.far]="i > highlightedStar"
           (click)="rate(i)"
           (mouseenter)="highlightedStar = i"
           (mouseleave)="highlightedStar = 0"></i>
      </div>
      <div class="text-center" *ngIf="step === 'prefer'">
        <i class="fas fa-star giant-text text-primary"></i>
        <h4>Thanks!</h4>
        <hr>
        <img class="w-25" [src]="post.writer.avatar" *ngIf="showAvatar" (error)="showAvatar = false" />
        <p [class.small-top-buffer]="showAvatar">
          You rated {{ writerFirstName() }}'s submission {{ post?.appRating?.value }} stars. If you'd like {{ writerFirstName() }} to write more for you in the future, click Prefer.
        </p>
        <button class="btn btn-secondary mr-3" (click)="prefer()">Prefer</button>
        <button class="btn btn-default" (click)="onDone()">Not now</button>
      </div>
      <div class="text-center" *ngIf="step === 'block'">
        <p class="top-buffer">
          Thanks for the feedback. If you don't want {{ writerFirstName() }} to write for you in the future, click Block.
        </p>
        <button class="btn btn-default small-right-buffer" (click)="onDone()">Not now</button>
        <button class="btn btn-danger" (click)="block()">Block</button>
      </div>
    </modal>
  `
})
export class RatePostModal implements OnInit {
  @Input() post: Post;
  @Input() action: 'purchase' | 'decline';
  @Output() done: EventEmitter<null> = new EventEmitter<null>();
  step: 'rate' | 'prefer' | 'block' = 'rate';
  stars: number[] = [1, 2, 3, 4, 5];
  highlightedStar: number = 0;
  showAvatar: boolean = true;
  postRating: number // For saving it after final step

  constructor(
    private postActions: PostActions,
    private customerWriterAccessActions: CustomerWriterAccessActions,
    private mutations: SubmissionMutations
  ) {}

  ngOnInit(): void {
    if (this.post.appRating) this.step = this.action === 'purchase' ? 'prefer' : 'block'
  }

  rate(n: number): void {
    this.postRating = n
    if (this.postRating >= 4 && this.action === 'purchase' && !this.post.writerPreferred) {
      this.step = 'prefer'
    } else if (n <= 2 && this.action === 'decline' && !this.post.writerBlocked) {
      this.step = 'block'
    } else {
      this.onDone()
    }
  }

  prefer(): void {
    this.customerWriterAccessActions.updateCustomerWriterAccessForAccount(this.post.writerId, { access: 'preferred' })
    this.onDone()
  }

  block(): void {
    this.customerWriterAccessActions.updateCustomerWriterAccessForAccount(this.post.writerId, { access: 'blocked' })
    this.postActions.loadPostsWithRelatedData(this.post.customerId, 'queued')
    this.onDone()
  }

  onDone(): void {
    this.done.emit(null)
    if (this.postRating)
      this.mutations.updateRating({ customerId: this.post.customerId, id: this.post.id, updates: { value: this.postRating } })
  }

  writerFirstName(): string {
    return this.post.writer.displayName.split(' ')[0]
  }
}
